import { BigNumber } from 'bignumber.js';
import { convertToBigNumber } from '@paradigm/utils/formatDecimal';
import { Maybe, Nullable } from '@paradigm/utils/types';
import { Bbo } from '#/unified-rfqs/entities/drfq/bbo';
import { DrfqLeg } from '#/unified-rfqs/entities/drfq/domain';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { GrfqLeg } from '#/unified-rfqs/entities/grfq/domain';
import { isOptionProduct } from '#/products/domain';
import { Rfq } from '#/unified-rfqs/entities/rfq';

interface BboOffsetArgs {
  readonly bidPrice: Maybe<string>;
  readonly askPrice: Maybe<string>;
  readonly bestBidQty: Maybe<string>;
  readonly bestBidPrice: Maybe<string>;
  readonly bestAskQty: Maybe<string>;
  readonly bestAskPrice: Maybe<string>;
  readonly productCode: Maybe<string>;
  readonly formatAsk?: (price: Maybe<string>) => string;
  readonly formatBid?: (price: Maybe<string>) => string;
}

export function getBboOffset({
  bidPrice,
  askPrice,
  bestBidQty,
  bestBidPrice,
  bestAskQty,
  bestAskPrice,
  formatBid,
  formatAsk,
}: BboOffsetArgs) {
  const screenBidPrice = getScreenPrice(bestBidQty, bestBidPrice);
  const screenAskPrice = getScreenPrice(bestAskQty, bestAskPrice);

  const bidDiff = getDiffValue(bidPrice, screenBidPrice);
  const askDiff = getDiffValue(askPrice, screenAskPrice);
  const offsetBid = formatValue(bidDiff, formatBid);
  const offsetAsk = formatValue(askDiff, formatAsk);
  const isBetterThanScreenBid = bidDiff != null && bidDiff.gt(0);
  const isBetterThanScreenAsk = askDiff != null && askDiff.lt(0);

  return {
    offsetBid,
    offsetAsk,
    isBetterThanScreenBid,
    isBetterThanScreenAsk,
  };
}

function getDiffValue(
  price: string | null | undefined,
  bboPrice: string | null | undefined,
) {
  if (price == null || bboPrice == null) return null;
  const diff = BigNumber(price).minus(bboPrice);
  return diff;
}

function formatValue(
  value: Nullable<BigNumber>,
  formatter?: (price: Maybe<string>) => string,
) {
  if (value == null) return null;
  const formatted =
    formatter != null ? formatter(value.toString()) : value.toString();
  if (value.isGreaterThan(0)) {
    return `+${formatted}`;
  }

  return formatted;
}

export function parseIv(value: string | null | undefined) {
  if (value == null) return null;
  return convertToBigNumber(value).isZero() ? null : value;
}

export function getScreenPrice(
  quantity: Maybe<string>,
  price: Maybe<string>,
): Maybe<string> {
  const bestQtyNumber = new BigNumber(quantity ?? 0);
  const isZeroQty = bestQtyNumber.isZero();
  const bestPrice = isZeroQty ? null : price ?? null;
  return bestPrice;
}

export function getBboLegs(bbo: GrfqBbo | Bbo | null) {
  if (bbo == null) return null;
  return 'legs' in bbo ? bbo.legs : bbo.leg_prices;
}

export function getScreenQty(quantity: Maybe<string>) {
  const bestQtyNumber = new BigNumber(quantity ?? 0);
  const isZeroQty = bestQtyNumber.isZero();

  return isZeroQty ? null : bestQtyNumber.toString();
}

/**
 *
 * Iv values should only be displayed accross the app if:
 * - rfq has a single option leg
 *
 * This check is used by bbo view so the formatted value displays
 * the iv number or an empty placeholder
 *
 * @param rfq
 * @returns {boolean}
 */
export function shouldShowIv(rfq: Nullable<Rfq>): boolean {
  if (rfq == null) {
    return false;
  }
  const { legs, product_codes } = rfq;
  const regularLegs = (legs as (GrfqLeg | DrfqLeg)[]).filter(
    (leg) => !leg.isHedge,
  );
  const isSingleLeg = regularLegs.length === 1;
  const haveOptions = product_codes.some(isOptionProduct);

  return isSingleLeg && haveOptions;
}
